import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  makeStyles,
  Spinner,
  Textarea,
  Text,
  tokens,
} from '@fluentui/react-components';
import { Info16Regular } from '@fluentui/react-icons';
import React, { useState, useEffect } from 'react';
import { useRegenerateReportMutation } from '../../queries/recording';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  infoIcon: {
    color: tokens.colorBrandForeground1,
    marginTop: '2px',
  },
  textarea: {
    minHeight: '120px',
  },
});

interface RegenerateReportDialogProps {
  recordingId: string | null;
  currentContext?: string;
  onClose: () => void;
}

export const RegenerateReportDialog = ({
  recordingId,
  currentContext,
  onClose,
}: RegenerateReportDialogProps) => {
  const styles = useStyles();
  const [userContext, setUserContext] = useState(currentContext || '');
  const { mutateAsync, isPending } = useRegenerateReportMutation();

  useEffect(() => {
    if (recordingId) {
      setUserContext(currentContext || '');
    }
  }, [recordingId, currentContext]);

  const handleRegenerate = async () => {
    if (!recordingId) return;
    await mutateAsync({
      recordingId,
      userContext: userContext.trim(),
    });
    onClose();
  };

  return (
    <Dialog
      open={!!recordingId}
      onOpenChange={(_, data) => !data.open && onClose()}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Regénérer le compte-rendu</DialogTitle>
          <DialogContent className={styles.content}>
            <div className={styles.infoBox}>
              <Info16Regular className={styles.infoIcon} />
              <Text size={200}>
                La regeneration prendra en compte les modifications apportees au
                transcript et le contexte que vous fournissez ci-dessous.
              </Text>
            </div>
            <Field
              label="Contexte (optionnel)"
              hint="Precisez des acronymes, noms de produits, l'objectif de la reunion, etc."
            >
              <Textarea
                className={styles.textarea}
                value={userContext}
                onChange={(_, data) => setUserContext(data.value)}
                placeholder="Ex: ACME est notre client principal. Le projet Phoenix concerne la refonte du SI..."
                maxLength={2000}
              />
            </Field>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={onClose}
              disabled={isPending}
            >
              Annuler
            </Button>
            <Button
              appearance="primary"
              onClick={handleRegenerate}
              disabled={isPending}
            >
              {isPending ? <Spinner size="tiny" /> : 'Regénérer'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
